import React from "react";
import { Box, Stack, Typography, Button, CircularProgress } from "@mui/material";
import { styled } from "@mui/system";
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { useApiManager } from "lib/ApiContext";
import ActivityRegistrationDialog from "./ActivityRegistrationDialog";

// v2: tokens de docs/IDENTIDAD_VISUAL.md
const DetailCard = styled(Box)({
  backgroundColor: "#14141F",
  borderRadius: "10px",
  padding: "20px",
  color: "#EDE9F7",
  border: "1px solid rgba(237,233,247,0.08)",
});

const SectionTitle = styled(Typography)({
  color: "#EDE9F7",
  fontSize: "18px",
  fontWeight: 700,
  marginBottom: "16px",
  fontFamily: "'Space Grotesk', sans-serif",
});

const DetailItem = styled(Stack)({
  flexDirection: "row",
  alignItems: "center",
  gap: "12px",
  marginBottom: "12px",
  color: "#A8A3B8",
});

const IconWrapper = styled(Box)({
  color: "#C77DFF",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "24px",
  height: "24px",
});

const RegisterButton = styled(Button)({
  backgroundColor: "#6A00CC",
  color: "white",
  padding: "12px",
  fontSize: "15px",
  fontWeight: "600",
  borderRadius: "10px",
  textTransform: "none",
  width: "100%",
  marginTop: "8px",
  "&:hover": {
    backgroundColor: "#9D00FF",
    boxShadow: "0 0 12px rgba(157,0,255,.4)",
  },
  "&:disabled": {
    backgroundColor: "#1E1B2E",
    color: "#6E6880",
  }
});

const WarningText = styled(Typography)({
  color: "#FF5C5C",
  fontSize: "13px",
  marginTop: "8px",
  textAlign: "center",
  fontWeight: "500",
});

const SuccessText = styled(Typography)({
  color: "#00E08F",
  fontSize: "13px",
  marginTop: "8px",
  textAlign: "center",
  fontWeight: "500",
});

const InfoBox = styled(Box)({
  backgroundColor: "#1E1B2E",
  border: "1px solid rgba(237,233,247,0.08)",
  borderRadius: "10px",
  padding: "12px",
  marginTop: "12px",
  textAlign: "center",
});

const EventDetailsCard = ({ event, onRegisterSuccess }) => {
  const api = useApiManager();
  
  // Estados para el diálogo
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [isRegistering, setIsRegistering] = React.useState(false);
  const [qrToken, setQrToken] = React.useState(null);
  const [registrationError, setRegistrationError] = React.useState(null);
  const [registrationStatus, setRegistrationStatus] = React.useState(null);

  if (!event) return null;

  const parseValue = (value) => {
    if (!value) return null;
    if (value.S) return value.S;
    if (value.N) return parseInt(value.N);
    if (value.BOOL !== undefined) return value.BOOL;
    return value;
  };

  const eventData = {
    EventID: parseValue(event.EventID) || event.EventID,
    EventName: parseValue(event.EventName) || event.EventName,
    EventDate: parseValue(event.EventDate) || event.EventDate,
    Horario: parseValue(event.Horario) || event.Horario,
    Lugar: parseValue(event.Lugar) || event.Lugar,
    CupoLimitado: parseValue(event.CupoLimitado) ?? event.CupoLimitado,
    CupoMaximo: parseValue(event.CupoMaximo) || event.CupoMaximo || 0,
    Registrados: parseValue(event.Registrados) || event.Registrados || 0,
    RequiereEntradas: parseValue(event.RequiereEntradas) ?? event.RequiereEntradas,
    Comments: parseValue(event.Comments) || event.Comments || 0,
    Likes: parseValue(event.Likes) || event.Likes || 0,
    Status: parseValue(event.Status) || event.Status || 0,
  };

  const availableSpots = eventData.CupoLimitado
    ? eventData.CupoMaximo - eventData.Registrados
    : null;

  const isFull = eventData.CupoLimitado && availableSpots <= 0;

  const formatSchedule = (horario) => {
    if (!horario) return ["Horario por confirmar"];
    let lines = horario.split(/\n+/).map(l => l.trim()).filter(l => l);
    if (lines.length === 1) {
      const parts = horario.split(/(?<=[ap]m)\s+(?=[A-ZÁÉÍÓÚ])/);
      if (parts.length > 1) return parts.map(p => p.trim()).filter(p => p);
    }
    return lines;
  };

  const scheduleLines = formatSchedule(eventData.Horario);

  const handleRegisterClick = async () => {
    console.log("🔵 Botón 'Registrarme' clickeado");
    
    // Resetear estados
    setQrToken(null);
    setRegistrationError(null);
    
    // Abrir el diálogo
    setDialogOpen(true);
    console.log("🔵 Diálogo abierto");
    
    // Iniciar registro
    setIsRegistering(true);
    
    try {
      // Intenta con el método correcto del API
      let response;
      if (api.RegisterToEvent) {
        response = await api.RegisterToEvent(eventData.EventID);
      } else if (api.RegisterParticipant) {
        response = await api.RegisterParticipant("1");
      } else {
        throw new Error("Método de registro no disponible en el API");
      }
      
      console.log("✅ Respuesta del registro:", response);
      
      // Si la respuesta es null (CORS u otro error), lanzar excepción
      if (!response) {
        throw new Error("No se recibió respuesta del servidor. Verifica la configuración de CORS en el backend.");
      }
      
      // Extraer el token del QR de la respuesta
      if (response.QRToken) {
        setQrToken(response.QRToken);
      } else if (response.qrToken) {
        setQrToken(response.qrToken);
      } else if (response.token) {
        setQrToken(response.token);
      } else if (response.success) {
        // Si solo retorna success, usar el EventID como token
        console.warn("⚠️ No se encontró QRToken en la respuesta, usando ID del evento");
        setQrToken(eventData.EventID.toString());
      } else {
        throw new Error("Respuesta del servidor no contiene token de registro");
      }
      
      setRegistrationStatus('success');
      if (onRegisterSuccess) onRegisterSuccess(eventData);
      
    } catch (error) {
      console.error("❌ Error al registrarse:", error);
      
      // Determinar el mensaje de error apropiado
      let errorMessage = "No se pudo completar el registro.";
      
      if (error.message && error.message.includes("CORS")) {
        errorMessage = "Error de conexión con el servidor. El backend necesita habilitar CORS para localhost:3000";
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.status === 401) {
        errorMessage = "No estás autenticado. Por favor, inicia sesión nuevamente.";
      } else if (error.response?.status === 400) {
        errorMessage = "Ya estás registrado en este evento o los datos son inválidos.";
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setRegistrationError(errorMessage);
      setRegistrationStatus('error');
      
    } finally {
      setIsRegistering(false);
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setQrToken(null);
    setRegistrationError(null);
  };

  return (
    <>
      <DetailCard>
        <Box sx={{ mr: 0 }}>
          <SectionTitle>Detalles del evento</SectionTitle>

          <Stack spacing={1.5} sx={{ marginBottom: 2 }}>
            {/* Fecha */}
            <DetailItem>
              <IconWrapper>
                <CalendarTodayIcon sx={{ fontSize: 20 }} />
              </IconWrapper>
              <Typography variant="body1" sx={{ lineHeight: 1.4 }}>
                {eventData.EventDate || "Fecha por confirmar"}
              </Typography>
            </DetailItem>

            {/* Horario */}
            <DetailItem
              sx={{
                alignItems: "flex-start",
                justifyContent: "flex-start",
              }}
            >
              <IconWrapper sx={{ marginTop: "2px" }}>
                <AccessTimeIcon sx={{ fontSize: 20 }} />
              </IconWrapper>

              <Box sx={{ flex: 1, textAlign: "left" }}>
                {scheduleLines.map((line, index) => (
                  <Typography
                    key={index}
                    variant="body1"
                    sx={{
                      lineHeight: 1.4,
                      color: "#A8A3B8",
                      textAlign: "left",
                      display: "block",
                    }}
                  >
                    {line}
                  </Typography>
                ))}
              </Box>
            </DetailItem>

            {/* Lugar */}
            <DetailItem>
              <IconWrapper>
                <LocationOnIcon sx={{ fontSize: 20 }} />
              </IconWrapper>
              <Typography variant="body1" sx={{ lineHeight: 1.4 }}>
                {eventData.Lugar || "Lugar por confirmar"}
              </Typography>
            </DetailItem>
          </Stack>

          {eventData.CupoLimitado && availableSpots !== null && (
            <InfoBox>
              <Typography variant="body2" sx={{ color: "#A8A3B8", mb: 0.5 }}>
                Cupos disponibles
              </Typography>
              <Typography
                variant="h4"
                sx={{
                  color: availableSpots > 10 ? "#00E08F" : availableSpots > 0 ? "#FFD60A" : "#FF5C5C",
                  fontWeight: "700",
                  fontSize: "2rem",
                  fontFamily: "'Space Grotesk', sans-serif"
                }}
              >
                {availableSpots}
              </Typography>
              <Typography variant="caption" sx={{ color: "#A8A3B8" }}>
                de {eventData.CupoMaximo} totales
              </Typography>
            </InfoBox>
          )}

          <Box sx={{ mt: 2 }}>
            <RegisterButton
              onClick={handleRegisterClick}
              disabled={isFull || registrationStatus === 'success'}
            >
              {registrationStatus === 'success' ? (
                "✓ Registrado"
              ) : isFull ? (
                "Cupos agotados"
              ) : (
                "Registrarme"
              )}
            </RegisterButton>

            {registrationStatus === 'success' && !dialogOpen && (
              <SuccessText>
                ✓ ¡Registro exitoso! Te esperamos en el evento
              </SuccessText>
            )}

            {registrationStatus === 'error' && !dialogOpen && (
              <WarningText>
                ⚠️ Error al registrarse. Intenta nuevamente
              </WarningText>
            )}

            {!registrationStatus && eventData.CupoLimitado && availableSpots > 0 && availableSpots <= 10 && (
              <WarningText>
                ⚠️ ¡Últimos {availableSpots} cupos disponibles!
              </WarningText>
            )}

            {eventData.RequiereEntradas && (
              <WarningText sx={{ mt: 1 }}>
                💳 Requiere comprar entrada
              </WarningText>
            )}
          </Box>

          <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid rgba(237,233,247,0.08)" }}>
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography variant="body2" sx={{ color: "#A8A3B8" }}>
                Registrados
              </Typography>
              <Typography variant="body2" sx={{ color: "#EDE9F7", fontWeight: 600 }}>
                {eventData.Registrados}
              </Typography>
            </Stack>

            <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography variant="body2" sx={{ color: "#A8A3B8" }}>
                Comentarios
              </Typography>
              <Typography variant="body2" sx={{ color: "#EDE9F7", fontWeight: 600 }}>
                {eventData.Comments}
              </Typography>
            </Stack>

            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" sx={{ color: "#A8A3B8" }}>
                Me gusta
              </Typography>
              <Typography variant="body2" sx={{ color: "#EDE9F7", fontWeight: 600 }}>
                {eventData.Likes}
              </Typography>
            </Stack>
          </Box>
        </Box>
      </DetailCard>

      {/* DIÁLOGO DE REGISTRO */}
      <ActivityRegistrationDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        loading={isRegistering}
        qrToken={qrToken}
        error={registrationError}
        activityName={eventData.EventName}
      />
    </>
  );
};

export default EventDetailsCard;